import {
  OFFER_ONLY_SANTE,
  TYPE_DEPENSE_PRIX_MOYEN,
  TYPE_DEPENSE_PRIX_PLV,
  TYPE_DEPENSE_PRIX_ZERO,
  TYPE_MOYENNE_NATIONALE,
} from '~/constants/simulateur'
import { RootState } from '~/store/state'
import { Offer } from '~/types/offers'
import { Parcours } from '~/types/parcours'
import { Acte, CardDataViz } from '~/types/simulateur'

export const getStepName = (stepData: any): string => {
  if (stepData.tagSousType !== '' && stepData.tagSousType) {
    return camelCaseConverter(stepData.tagSousType)
  }
  if (stepData.tagTypeSoin !== '' && stepData.tagTypeSoin) {
    return camelCaseConverter(stepData.tagTypeSoin)
  }

  return camelCaseConverter(stepData.titreCourt)
}

const formatAmount = (amount: number) => {
  const decimalPart = amount.toString().split('.')[1] || ''

  return amount.toLocaleString('fr-FR', {
    minimumFractionDigits: decimalPart.length === 1 ? 2 : 0,
    maximumFractionDigits: 2,
  })
}

export const calculateCardDataVizHeight = (): number => {
  // 1. Get DOM elements
  const wrapper = document.querySelector('.card-data-viz-wrapper')
  if (!wrapper) return 0

  // 2. Get child elements and their heights
  const children = Array.from(wrapper.children) as HTMLElement[]
  const childHeights = children.map((child) => child.offsetHeight)

  // 3. Determine screen breakpoint (adjust 768 to match your mobile breakpoint)
  const isMobile = window.innerWidth <= 768

  // 4. Calculate based on layout
  if (isMobile) {
    // Mobile (vertical layout): sum heights + gaps
    const gaps = (children.length - 1) * 32 // 32px gap from mobile media query
    return childHeights.reduce((acc, height) => acc + height, 0) + gaps
  }

  // Desktop (horizontal layout): height = tallest child
  return Math.max(...childHeights)
}

export const resultsToCardVizResults = (
  results: Acte[],
  offre: Offer,
  isRecommandedOffer: boolean,
  offerId: number,
  isSwitch = false,
  isOptam: boolean,
  resultsNoeud: Parcours
): CardDataViz => {
  const state = (useNuxtApp().$store as { state: RootState }).state
  const cumul = results.find((result) => result.acte === 'CUMUL') as Acte
  const actes = results.filter((result) => result.acte !== 'CUMUL')
  const selectedActe = JSON.parse(
    state.form.simulator.selectedActe as string
  ) as Parcours

  const getMention = (): string => {
    if (isSwitch && !isOptam) {
      const phraseMontant = selectedActe.actesSwitch?.[0]?.phraseMontant
      return phraseMontant ? `*${phraseMontant}` : ''
    }

    switch (selectedActe.typeDepense) {
      case TYPE_DEPENSE_PRIX_ZERO:
      case TYPE_DEPENSE_PRIX_PLV: {
        const phrase = isSwitch
          ? selectedActe.actesSwitch?.[0]?.phraseMontant
          : selectedActe?.phraseMontant
        return phrase ? `*${phrase}` : ''
      }
      default:
        return state.form.simulator?.amount0
          ? ''
          : state.form.simulator?.averagesPrices?.[
              selectedActe.codeActe as string
            ]?.typeMoyenne === TYPE_MOYENNE_NATIONALE
          ? '*Prix moyen constaté auprès de nos assurés.'
          : `*Prix moyen constaté auprès de nos assurés sur le département ${
              state.form.simulator?.averagesPrices?.[
                selectedActe.codeActe as string
              ]?.patriculeDepartement
            }.`
    }
  }

  const details = actes.map((result) => {
    let currentActe: Parcours | undefined
    const idNoeud = parseInt(result.idNoeud as string)
    const isFirstActe = result.acte === 'ACTE1'

    if (isSwitch) {
      currentActe =
        selectedActe.actesSwitch?.find(
          (acte: Parcours) => idNoeud === parseInt(acte.id)
        ) ||
        selectedActe.actesComplementaire[0]?.actesSwitch?.find(
          (acte: Parcours) => idNoeud === parseInt(acte.id)
        )
    } else if (isFirstActe) {
      currentActe = resultsNoeud
    } else {
      currentActe = resultsNoeud?.actesComplementaire[0]
    }

    const placeHolder = isFirstActe
      ? resultsNoeud?.placeHolder
      : resultsNoeud?.actesComplementaire[0]?.placeHolder

    return {
      label: placeHolder || currentActe?.titre,
      items: [
        {
          graph: {
            percent: null,
          },
          legend: `${formatAmount(result.montantRO)}€ par la sécurité Sociale`,
          belowLegend: '',
          color: '#0071CC',
        },
        {
          graph: {
            percent: null,
          },
          legend: `${formatAmount(result.montantRC)}€ par APRIL`,
          belowLegend: '',
          color: '#639e30',
        },
        {
          graph: {
            percent: null,
          },
          legend: `${formatAmount(result.montantRAC)}€ à votre charge`,
          showBelowLegendInfoIcon: true,
          color: '#D98508',
        },
      ],
    }
  })

  return {
    offer: offre,
    dataViz: {
      items: [
        {
          graph: {
            percent: (cumul.montantRO / cumul.depenseReelle) * 100,
          },
          legend: `${formatAmount(cumul.montantRO)}€ par la sécurité Sociale`,
          belowLegend: '',
          color: '#0071CC',
        },
        {
          graph: {
            percent: (cumul.montantRC / cumul.depenseReelle) * 100,
          },
          legend: `${formatAmount(cumul.montantRC)}€ par APRIL`,
          belowLegend: '',
          color: '#639e30',
        },
        {
          graph: {
            percent: (cumul.montantRAC / cumul.depenseReelle) * 100,
          },
          legend: `${formatAmount(cumul.montantRAC)}€ à votre charge`,
          showBelowLegendInfoIcon: true,
          color: '#D98508',
        },
      ],
    },
    withProductLayout: isRecommandedOffer ? 'Featured' : 'Basic',
    topTitle: `Avec l'offre ${extractProductNameWithLevel(
      offre?.productLabel || '',
      offre?.levelCode || '',
      'niveau'
    )}`,
    title: `${formatAmount(cumul.montantRAC)}€`,
    subtitle: `restant à votre charge sur un montant de ${
      cumul.depenseReelle
    }€${getMention() !== '' ? '*' : ''}`,
    mention: getMention(),
    ctaLabel: `Obtenir mon devis à ${formatAmount(
      offre.globalPriceWithoutOption
    )}€${toSuperscript(`(${offerId + 1})`)}`,
    linkLabel:
      offre.marketingProductId !== OFFER_ONLY_SANTE
        ? 'Souscrire en ligne'
        : undefined,
    bigTitle: true,
    accordionLabel: 'Détails du remboursement',
    dataVizDetails: actes.length > 1 ? (details as any) : undefined,
  }
}

const mappingRegime = {
  SS: '01',
  TNS: '02',
  Agricole: '04',
  AlsaceMoselle: '05',
} as const

type RegimeKey = keyof typeof mappingRegime

export const buildActeArray = (
  rootState: RootState,
  selectedActe: Parcours,
  isSwitch = false
): { idNoeud: string; depense: number }[] => {
  const resolveDepense = (acte: Parcours, isPrincipal = false): number => {
    const amount = isPrincipal
      ? Number(rootState.form.simulator.amount0)
      : Number(rootState.form.simulator.amount1)

    switch (acte.typeDepense) {
      case TYPE_DEPENSE_PRIX_MOYEN: {
        const averages = rootState.form.simulator.averagesPrices
        const code = acte.codeActe
        const fallbackAverage =
          code && averages ? averages[code]?.moyenne : undefined
        return amount > 0 ? amount : fallbackAverage ?? 0
      }
      case 'PRIXZERO':
        return 0
      case 'PRIXPLV':
        return acte.plvs?.[0]?.montant ?? amount
      case 'PRIXBR':
        return acte.baseRemboursementList?.[0]?.montant ?? amount
      case null:
      default:
        return amount
    }
  }

  let principal = {} as { idNoeud: string; depense: number }

  try {
    if (isSwitch) {
      principal = {
        idNoeud: (selectedActe.actesSwitch as Parcours[])[0].id,
        depense: resolveDepense(
          (selectedActe.actesSwitch as Parcours[])[0],
          true
        ),
      }
    } else {
      principal = {
        idNoeud: selectedActe.id,
        depense: resolveDepense(selectedActe, true),
      }
    }
  } catch (e) {
    console.error('Erreur dans principal', e)
  }

  const complementaires = Array.isArray(selectedActe.actesComplementaire)
    ? selectedActe.actesComplementaire.map((acte: Parcours) => {
        if (isSwitch) {
          return {
            idNoeud: (acte.actesSwitch as Parcours[])[0].id,
            depense: resolveDepense((acte.actesSwitch as Parcours[])[0]),
          }
        }
        return {
          idNoeud: acte.id,
          depense: resolveDepense(acte),
        }
      })
    : []

  return [principal, ...complementaires]
}

export const resolveBeneficiaire = (
  beneficiaryRaw?: string
): 'ASSURE' | 'CONJOINT' | 'ENFANT' => {
  const prefix = beneficiaryRaw?.split('_')[0]
  if (prefix === '0') return 'ASSURE'
  if (prefix === '1') return 'CONJOINT'
  return 'ENFANT'
}

export const resolveRegimeCode = (regimeKey?: string): string | undefined => {
  return mappingRegime[regimeKey as RegimeKey]
}

export const buildSimulationRequest = async (
  baseUrl: string,
  offer: Offer,
  context: {
    codeRegime: string | undefined
    beneficiaire: string
    codePostal: string
    actes: { idNoeud: string; depense: number }[]
  }
): Promise<any> => {
  const codesGarantiesBase = offer.warranties.flatMap((r) =>
    r.supplierMappings.AS400.map((u) => u.code)
  )

  const codesNiveauxBase = offer.warranties.flatMap((r) =>
    r.supplierMappings.AS400.map((u) => u.level)
  )

  return fetch(`${baseUrl}/api/simulator-calcul`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      codeProduitBase: offer.supplierProductCodes.AS400,
      codesGarantiesBase,
      codesNiveauxBase,
      codeProduitSurco: '',
      codesGarantiesSurco: [],
      codesNiveauxSurco: [],
      codeRegime: context.codeRegime,
      beneficiaire: context.beneficiaire,
      codePostal: context.codePostal,
      actes: context.actes,
    }),
  }).then((res) => res.json().then((json) => json.data))
}

export const isRecommandedOffer = (
  comparator: any,
  offre: Offer,
  isLast: boolean
): boolean => {
  if (comparator) {
    return (
      offre.marketingProductId === comparator?.marketingProductId &&
      offre.levelCode === comparator?.levelCode
    )
  } else {
    return isLast
  }
}

const toSuperscript = (num: string) => {
  const sup = {
    '0': '⁰',
    '1': '¹',
    '2': '²',
    '3': '³',
    '4': '⁴',
    '5': '⁵',
    '6': '⁶',
    '7': '⁷',
    '8': '⁸',
    '9': '⁹',
    '(': '⁽',
    ')': '⁾',
  }
  return String(num)
    .split('')
    .map((d) => sup[d as keyof typeof sup] || d)
    .join('')
}
